import { z } from "zod";

/**
 * A value of a server enum that THIS build of the contract does not list —
 * still the exact string the server sent, branded so the compiler can tell it
 * from a listed one.
 *
 * Kept as the raw string rather than replaced by a placeholder, because the
 * only honest thing a reader can say about it is the server's own word: an
 * operator reading "CONSOLE_API (not known to this version)" learns something;
 * one reading "Unknown" learns nothing and cannot even report it.
 */
export type UnlistedEnumValue = z.infer<typeof UnlistedEnumValueSchema>;

const UnlistedEnumValueSchema = z.string().brand<"UnlistedEnumValue">();

/**
 * The READ side of a server enum: every listed value parses as itself, and a
 * value this contract does not list parses as an {@link UnlistedEnumValue}
 * instead of failing the response it sits in.
 *
 * ── Why a separate read schema exists at all ───────────────────────────────
 *
 * The console (Vercel) and the backend (Porter) ship independently, a browser
 * tab keeps the bundle it loaded, and a published CLI keeps its version for
 * weeks. So a backend that has learned a new value serves it to readers that
 * have not. Under a strict `z.enum` that one unknown string fails the WHOLE
 * parse in the generated hook: a new trigger source blanks the deployments
 * list, not just one label in it. Here it degrades to one unlisted field.
 *
 * ── Why the brand, and not a plain `string` ────────────────────────────────
 *
 * The output type is `ListedValue | UnlistedEnumValue`, which is NOT `string`:
 * an exhaustive `Record<ListedValue, …>` cannot be indexed with it, so every
 * reader has to decide what an unlisted value renders as before it compiles,
 * and a value the contract LISTS later is a compile error at every reader until
 * it has words. Widening to `string` would make the parse lenient and every
 * reader silently wrong.
 *
 * ── Where this must NOT be used ────────────────────────────────────────────
 *
 * Only for a field whose unknown value has an honest rendering — a label, a
 * provenance, an echo of the request. A field readers make DECISIONS on (a
 * lifecycle status: is it in flight, may it be cancelled, has it shipped) has
 * no truthful generic answer for a value nobody can classify, and stays strict;
 * its protection is a reader list that leads the database, not leniency.
 *
 * The emitting side never uses this: a backend writing a value it does not
 * list is a bug, and the strict enum is what reports it.
 */
export function enumReadSchema<const V extends readonly [string, ...string[]]>(values: V) {
  return z.union([z.enum(values), unlistedEnumValueSchema(values)]);
}

/**
 * The UNLISTED half of {@link enumReadSchema} on its own: a string `values` does
 * not contain, branded {@link UnlistedEnumValue}. A listed value is refused here,
 * so it can only ever parse as itself.
 *
 * Exported for a reader that has to route the two halves to different shapes
 * rather than to one field — the audit feed's unlisted-event arm, whose event
 * type is this and whose fields are whatever the server wrote.
 */
export function unlistedEnumValueSchema(values: readonly string[]) {
  const listed: ReadonlySet<string> = new Set(values);
  return UnlistedEnumValueSchema.refine((value) => !listed.has(value), {
    message: "a listed value must parse as itself, never as unlisted"
  });
}

/**
 * Narrows a read value to the listed ones, so a reader can switch on it
 * exhaustively after it has rendered the unlisted case.
 */
export function isListedEnumValue<const V extends readonly string[]>(
  values: V,
  value: V[number] | UnlistedEnumValue
): value is V[number] {
  const listed: readonly string[] = values;
  return listed.includes(value);
}
